import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, apiGet } from "../api";
import { Button } from "../components/ui/button";
import {
  Boxes, Zap, Download, CheckCircle2, RefreshCw, ChevronDown, ChevronRight, FileDown,
} from "lucide-react";
import usePageTitle from "../hooks/usePageTitle";
import { formatMoney } from "../lib/format";

/**
 * Records Updater Workspace (/records-updater)
 *
 * A focused view for the person who keys approved prices into WMS and
 * Kraken. Surfaces only what still needs work:
 *   • Submissions that are APPROVED but WMS and/or Kraken is still pending.
 *   • One-click "Mark entered" buttons per system per submission.
 *   • A SINGLE bulk-CSV export of every pending row across all submissions
 *     so the updater can paste them into the downstream systems in one go.
 *
 * Lives at a separate route from /portal-admin so the records updater
 * doesn't have to wade through the full admin surface to find their work.
 * (Still reachable from the admin nav for context.)
 */

const FILTERS = [
  { key: "any", label: "Any pending", help: "WMS or Kraken still to confirm" },
  { key: "both", label: "Not started", help: "Neither system confirmed" },
  { key: "wms", label: "WMS pending", help: "WMS still to confirm" },
  { key: "kraken", label: "Kraken pending", help: "Kraken still to confirm" },
];

const fmtPrice = (value, currency) => formatMoney(value, currency || "GBP");

export default function RecordsUpdater() {
  usePageTitle("Records Updater");
  const [pending, setPending] = useState("any");
  const [data, setData] = useState({ submissions: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet("/portal/admin/updater-queue", { params: { pending } });
      setData(d || { submissions: [], summary: null });
    } catch {
      toast.error("Failed to load workspace");
    } finally {
      setLoading(false);
    }
  }, [pending]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const bulkExport = () => {
    const url = `${API}/portal/admin/updater-queue/export.csv?pending=${pending}`;
    window.location.href = url;
  };

  const { summary, submissions } = data;

  return (
    <div style={{ padding: "0 1.5rem" }} data-testid="records-updater-page">
      <header style={{ marginBottom: "1.5rem", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: "1.75rem", fontWeight: 700, color: "var(--octopus-ice)", marginBottom: "0.35rem" }}>
            Records Updater Workspace
          </h1>
          <p style={{ color: "var(--octopus-voltage)", opacity: 0.8, fontSize: "0.9rem", maxWidth: "640px", margin: 0 }}>
            Approved price submissions still waiting to be entered into WMS or Kraken. Confirm each system once the prices are loaded — confirmations are locked once set.
          </p>
        </div>
        <Button
          onClick={load}
          className="btn btn-secondary"
          data-testid="refresh-queue-btn"
          style={{ padding: "0.45rem 0.85rem", fontSize: "0.85rem" }}
        >
          <RefreshCw size={13} style={{ marginRight: "0.35rem" }} /> Refresh
        </Button>
      </header>

      <SummaryStrip summary={summary} loading={loading} />

      <div className="card" style={{ marginTop: "1rem" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }} data-testid="pending-filter-chips">
            {FILTERS.map(f => (
              <button
                key={f.key}
                onClick={() => setPending(f.key)}
                title={f.help}
                data-testid={`filter-${f.key}`}
                style={{
                  padding: "0.4rem 0.85rem", borderRadius: "999px", fontSize: "0.8rem", fontWeight: 600,
                  background: pending === f.key ? "rgba(96,240,248,0.18)" : "rgba(10,18,42,0.5)",
                  color: pending === f.key ? "var(--octopus-ice)" : "var(--octopus-voltage)",
                  border: `1px solid ${pending === f.key ? "rgba(96,240,248,0.6)" : "rgba(96,240,248,0.18)"}`,
                  cursor: "pointer", transition: "all 0.15s",
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
          <Button
            onClick={bulkExport}
            disabled={loading || (submissions || []).length === 0}
            className="btn btn-primary"
            data-testid="bulk-export-btn"
            style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }}
          >
            <FileDown size={14} style={{ marginRight: "0.4rem" }} />
            Export all pending ({submissions?.length || 0})
          </Button>
        </div>

        {loading ? (
          <p style={{ textAlign: "center", padding: "2rem", opacity: 0.6 }}>Loading…</p>
        ) : !submissions || submissions.length === 0 ? (
          <EmptyState />
        ) : (
          <div data-testid="queue-list" style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {submissions.map(s => (
              <QueueRow
                key={s.id}
                submission={s}
                expanded={!!expanded[s.id]}
                onToggle={() => toggle(s.id)}
                onUpdate={() => load()}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function SummaryStrip({ summary, loading }) {
  if (loading && !summary) {
    return null;
  }
  const s = summary || { total_submissions: 0, wms_pending: 0, kraken_pending: 0, total_skus: 0 };
  const cards = [
    { label: "Submissions in queue", value: s.total_submissions, accent: "var(--octopus-ice)" },
    { label: "SKUs to update", value: s.total_skus, accent: "var(--octopus-voltage)" },
    { label: "WMS pending", value: s.wms_pending, accent: "#FFD84F", icon: <Boxes size={14} /> },
    { label: "Kraken pending", value: s.kraken_pending, accent: "#F050F8", icon: <Zap size={14} /> },
  ];
  return (
    <div data-testid="summary-strip" style={{
      display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.75rem",
    }}>
      {cards.map(c => (
        <div key={c.label} className="card" style={{ padding: "0.85rem 1rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: "var(--octopus-voltage)", opacity: 0.75, fontSize: "0.7rem", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "0.35rem" }}>
            {c.icon} {c.label}
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 700, color: c.accent, fontFamily: "monospace" }}>
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}


function EmptyState() {
  return (
    <div data-testid="empty-state" style={{ textAlign: "center", padding: "3rem 1rem", color: "var(--octopus-voltage)" }}>
      <CheckCircle2 size={42} style={{ color: "#22c55e", marginBottom: "0.85rem" }} />
      <h3 style={{ color: "var(--octopus-ice)", marginBottom: "0.35rem" }}>All caught up</h3>
      <p style={{ opacity: 0.75, fontSize: "0.9rem", margin: 0 }}>
        No approved submissions are waiting for confirmation with the current filter.
      </p>
    </div>
  );
}


function QueueRow({ submission: s, expanded, onToggle, onUpdate }) {
  return (
    <div data-testid={`queue-row-${s.id}`} style={{
      border: "1px solid rgba(96,240,248,0.18)", borderRadius: "8px",
      background: "rgba(10,18,42,0.4)", overflow: "hidden",
    }}>
      <div style={{
        display: "grid", gridTemplateColumns: "auto 1fr auto auto", gap: "0.85rem",
        alignItems: "center", padding: "0.75rem 1rem",
      }}>
        <button
          onClick={onToggle}
          aria-label={expanded ? "Collapse" : "Expand"}
          data-testid={`toggle-${s.id}`}
          style={{ background: "transparent", border: "none", color: "var(--octopus-voltage)", cursor: "pointer", display: "flex" }}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        <div>
          <div style={{ fontWeight: 600, color: "var(--octopus-ice)", marginBottom: "0.15rem" }}>
            {s.supplier_name}{" "}
            <span style={{ color: "var(--octopus-voltage)", opacity: 0.65, fontSize: "0.75rem", marginLeft: "0.35rem" }}>
              {s.supplier_category} · Ref {s.id.slice(0, 8)}
            </span>
            <span data-testid={`row-currency-${s.id}`} style={{
              marginLeft: "0.4rem", padding: "0.1rem 0.45rem", borderRadius: "999px",
              fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.04em",
              background: (s.currency && s.currency !== "GBP") ? "rgba(255,216,79,0.18)" : "rgba(96,240,248,0.1)",
              color: (s.currency && s.currency !== "GBP") ? "#FFD84F" : "var(--octopus-voltage)",
              border: `1px solid ${(s.currency && s.currency !== "GBP") ? "rgba(255,216,79,0.45)" : "rgba(96,240,248,0.2)"}`,
            }}>{s.currency || "GBP"}</span>
            {s.status === "auto_approved" && (
              <span data-testid={`fast-track-badge-${s.id}`} title="Auto-approved via fast-track token" style={{
                marginLeft: "0.4rem", padding: "0.1rem 0.45rem", borderRadius: "999px",
                fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.04em",
                background: "rgba(255,216,79,0.18)", color: "#FFD84F",
                border: "1px solid rgba(255,216,79,0.5)",
              }}>⚡ FAST-TRACK</span>
            )}
          </div>
          <div style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)", opacity: 0.7 }}>
            {s.sku_count} SKU{s.sku_count !== 1 ? "s" : ""} · Approved {new Date(s.reviewed_at).toLocaleString()}
            {s.reviewed_by ? <> by {s.reviewed_by}</> : null}
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <SystemTag label="WMS" icon={<Boxes size={12} />} done={!s.wms_pending} confirmedBy={s.wms_confirmed_by} confirmedAt={s.wms_confirmed_at} />
          <SystemTag label="Kraken" icon={<Zap size={12} />} done={!s.kraken_pending} confirmedBy={s.kraken_confirmed_by} confirmedAt={s.kraken_confirmed_at} />
        </div>

        <Button
          onClick={() => window.location.href = `${API}/portal/admin/submissions/${s.id}/csv`}
          className="btn btn-secondary"
          data-testid={`download-csv-${s.id}`}
          style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
        >
          <Download size={11} style={{ marginRight: "0.3rem" }} /> CSV
        </Button>
      </div>

      {expanded && (
        <ExpandedDetail submission={s} onUpdate={onUpdate} />
      )}
    </div>
  );
}


function SystemTag({ label, icon, done, confirmedBy, confirmedAt }) {
  const title = done
    ? `Confirmed by ${confirmedBy || "—"} at ${confirmedAt ? new Date(confirmedAt).toLocaleString() : ""}`
    : `${label} still pending`;
  return (
    <span
      title={title}
      data-testid={`system-tag-${label.toLowerCase()}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: "0.3rem", padding: "0.2rem 0.55rem",
        borderRadius: "999px", fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
        background: done ? "rgba(34,197,94,0.15)" : "rgba(255,216,79,0.12)",
        color: done ? "#86efac" : "#FFD84F",
        border: `1px solid ${done ? "rgba(34,197,94,0.45)" : "rgba(255,216,79,0.4)"}`,
      }}
    >
      {done ? <CheckCircle2 size={12} /> : icon}
      {label} {done ? "✓" : "•"}
    </span>
  );
}


function ExpandedDetail({ submission: s, onUpdate }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    apiGet(`/portal/admin/submissions/${s.id}`)
      .then(d => setRows(d?.rows || []))
      .catch(() => setRows([]));
  }, [s.id]);

  const confirm = async (system) => {
    setBusy(system);
    try {
      await axios.post(`${API}/portal/admin/submissions/${s.id}/confirm-system`, { system });
      toast.success(`Confirmed in ${system.toUpperCase()}`);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Confirmation failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ borderTop: "1px solid rgba(96,240,248,0.15)", padding: "0.85rem 1rem", background: "rgba(10,18,42,0.6)" }}>
      <div style={{ display: "flex", gap: "0.65rem", marginBottom: "0.85rem", flexWrap: "wrap" }}>
        {s.wms_pending ? (
          <Button
            onClick={() => confirm("wms")}
            disabled={busy === "wms"}
            className="btn btn-primary"
            data-testid={`confirm-wms-${s.id}`}
            style={{ padding: "0.4rem 0.85rem", fontSize: "0.8rem" }}
          >
            <Boxes size={13} style={{ marginRight: "0.35rem" }} />
            {busy === "wms" ? "Confirming…" : "Mark entered in WMS"}
          </Button>
        ) : null}
        {s.kraken_pending ? (
          <Button
            onClick={() => confirm("kraken")}
            disabled={busy === "kraken"}
            className="btn btn-primary"
            data-testid={`confirm-kraken-${s.id}`}
            style={{ padding: "0.4rem 0.85rem", fontSize: "0.8rem" }}
          >
            <Zap size={13} style={{ marginRight: "0.35rem" }} />
            {busy === "kraken" ? "Confirming…" : "Mark entered in Kraken"}
          </Button>
        ) : null}
        {!s.wms_pending && !s.kraken_pending && (
          <span style={{ color: "#86efac", fontSize: "0.85rem", display: "inline-flex", alignItems: "center", gap: "0.35rem" }}>
            <CheckCircle2 size={14} /> Both systems confirmed.
          </span>
        )}
      </div>

      {rows === null ? (
        <p style={{ opacity: 0.6, fontSize: "0.85rem", margin: 0 }}>Loading rows…</p>
      ) : rows.length === 0 ? (
        <p style={{ opacity: 0.6, fontSize: "0.85rem", margin: 0 }}>No rows on this submission.</p>
      ) : (
        <div style={{ maxHeight: "260px", overflowY: "auto", border: "1px solid rgba(96,240,248,0.12)", borderRadius: "6px" }}>
          <table className="data-table" data-testid={`rows-table-${s.id}`} style={{ marginBottom: 0, fontSize: "0.82rem" }}>
            <thead style={{ position: "sticky", top: 0, background: "rgba(10,18,42,0.98)" }}>
              <tr>
                <th>SKU</th>
                <th>Description</th>
                <th style={{ textAlign: "right" }}>New ({s.currency || "GBP"})</th>
                <th>Effective From</th>
                <th>Effective To</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ fontFamily: "monospace" }}>{r.sku}</td>
                  <td>{r.product_description || "—"}</td>
                  <td style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>{fmtPrice(r.new_price, s.currency || "GBP")}</td>
                  <td>{r.effective_date}</td>
                  <td style={{ fontStyle: r.effective_to ? "normal" : "italic", color: r.effective_to ? "var(--octopus-ice)" : "var(--octopus-voltage)" }}>
                    {r.effective_to || "Open-ended"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
